<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { Icon, statusInfo, type StatusKey } from './icons';
import { NAV_GROUPS, MOBILE_TABS, matchNav } from './navigation';
import SignalDot from './SignalDot.vue';
import StatusSwitch from './StatusSwitch.vue';
import PublicSiteStatusChip from './PublicSiteStatusChip.vue';
import { GREETER_NOT_OWNED, listGreeters, setGreeter } from '../api/internal';
import { HttpError } from '../api/http';
import { useToast } from '../composables/useToast';

const auth = useAuthStore();
const route = useRoute();
const router = useRouter();
const toast = useToast();

// localStorage 缓存上次成功拿到的"我"信息, 避免硬刷新时初始空状态闪 Cat 用户名.
// 用 wxworkUserId 做缓存 key, 同一账号切不同企微员工时各存各的 — 避免账号 A 看到账号 B 的头像.
const ME_CACHE_KEY = 'cat.internal.me';
interface MeCache { wxworkUserId: string; displayName: string | null; avatar: string | null; isActive: boolean; }
function readMeCache(): MeCache | null {
  try {
    const raw = localStorage.getItem(ME_CACHE_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw) as MeCache;
    // 缓存的 wxworkUserId 必须跟当前登录一致, 否则切换员工时会用错信息
    if (c.wxworkUserId !== auth.wxworkUserId) return null;
    return c;
  } catch { return null; }
}
const initialMe = readMeCache();

// 当前员工接客状态: online = 接客 (IsActiveGreeter=true), away = 不接客 (false)
const status = ref<StatusKey>(initialMe ? (initialMe.isActive ? 'online' : 'away') : 'online');
const statusBusy = ref(false);
const myAvatar = ref<string | null>(initialMe?.avatar ?? null);
const myWxworkName = ref<string | null>(initialMe?.displayName ?? null);

async function loadStatus() {
  // 没 token 时不调 API: OAuth callback 初次渲染瞬间 AppShell 可能短暂 mount,
  // listGreeters 401 → http.ts 清 token + replace('/') 会冲断 OAuth flow.
  // 每次 mount 都重拉刷新: localStorage 缓存只用于消除初始空状态闪烁.
  if (!auth.token) return;
  try {
    const greeters = await listGreeters(auth.wxworkUserId);
    const me = greeters.find((g) => g.isMe);
    if (me) {
      status.value = me.isActiveGreeter ? 'online' : 'away';
      myAvatar.value = me.avatar || null;
      myWxworkName.value = me.displayName || null;
      localStorage.setItem(ME_CACHE_KEY, JSON.stringify({
        wxworkUserId: auth.wxworkUserId,
        displayName: me.displayName ?? null,
        avatar: me.avatar ?? null,
        isActive: me.isActiveGreeter,
      } satisfies MeCache));
    }
  } catch {
    // 未绑 / 网络错误: 保留默认 online, 下次 mount 重试
  }
}

async function onStatusChange(next: StatusKey) {
  if (statusBusy.value) return;
  const prev = status.value;
  status.value = next;
  statusBusy.value = true;
  try {
    if (!auth.wxworkUserId) return;
    await setGreeter(auth.wxworkUserId, next === 'online');
  } catch (e) {
    status.value = prev;
    if (e instanceof HttpError && e.status === 403) toast.error(GREETER_NOT_OWNED);
    else toast.error(e);
  } finally {
    statusBusy.value = false;
  }
}

// ── 导航抽屉 (窄窗 / 手机) ──
const navOpen = ref(false);
const closeBtn = ref<HTMLButtonElement | null>(null);
async function openNav() {
  navOpen.value = true;
  await nextTick();
  closeBtn.value?.focus();
}
function closeNav() { navOpen.value = false; }
function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape' && navOpen.value) closeNav();
}
watch(() => route.fullPath, closeNav);

onMounted(() => {
  loadStatus();
  window.addEventListener('keydown', onKey);
});
onBeforeUnmount(() => window.removeEventListener('keydown', onKey));

const current = computed(() => matchNav(route.path));
const activePath = computed(() => current.value?.item.path ?? '');
const pageTitle = computed(() => (route.meta.title as string | undefined) || current.value?.item.label || '工作台');
const sectionLabel = computed(() => current.value?.group.label ?? '');

function go(path: string) {
  if (route.path === path) { closeNav(); return; }
  router.push(path);
}

// 优先用企微 name (中文名/英文名), 否则回退 Cat 用户名. 企微 name 异步从 listGreeters 拉.
const meName = computed(() => myWxworkName.value || auth.username || '工作台');
const meInitial = computed(() => (myWxworkName.value || auth.username || '?').charAt(0).toUpperCase());

function logout() {
  localStorage.removeItem(ME_CACHE_KEY);
  auth.logout();
  router.push('/');
}
</script>

<template>
  <div :class="['wb-shell', { 'nav-open': navOpen }]">
    <aside class="wb-side" aria-label="导航">
      <div class="wb-side-brand">
        <img class="logo-img" src="/logo.png" alt="喵喵小窝" />
        <div class="wordmark">喵喵小窝<span class="sub">员工工作台</span></div>
        <button ref="closeBtn" class="wb-top-icon wb-side-close" type="button" aria-label="关闭菜单" @click="closeNav">
          <span v-html="Icon.X" />
        </button>
      </div>

      <nav class="wb-side-nav">
        <template v-for="g in NAV_GROUPS" :key="g.label">
          <div class="wb-nav-section-label">{{ g.label }}</div>
          <button
            v-for="it in g.items"
            :key="it.path"
            type="button"
            :class="['wb-nav-item', activePath === it.path ? 'active' : '']"
            :aria-current="activePath === it.path ? 'page' : undefined"
            @click="go(it.path)"
          >
            <span class="nav-ic" v-html="it.icon" />
            <span class="label">{{ it.label }}</span>
          </button>
        </template>
      </nav>

      <div class="wb-side-footer">
        <div class="wb-me">
          <div class="av">
            <img v-if="myAvatar" :src="myAvatar" :alt="meName" class="av-img" referrerpolicy="no-referrer" @error="myAvatar = null" />
            <template v-else>{{ meInitial }}</template>
            <SignalDot :status="status" />
          </div>
          <div class="info">
            <div class="nm">{{ meName }}</div>
            <div class="stat">{{ statusInfo(status).label }}</div>
          </div>
        </div>
        <div class="wb-side-actions wb-side-drawer-only">
          <StatusSwitch :value="status" @change="onStatusChange" />
          <button class="btn btn-outline" type="button" @click="logout">
            <span v-html="Icon.Logout" />
            退出登录
          </button>
        </div>
      </div>
    </aside>

    <div class="wb-scrim" @click="closeNav" />

    <header class="wb-topbar">
      <button class="wb-top-icon wb-menu-btn" type="button" aria-label="打开菜单" @click="openNav">
        <span v-html="Icon.Menu" />
      </button>
      <div class="crumb">
        <template v-if="sectionLabel">
          <span class="sec">{{ sectionLabel }}</span>
          <span class="sep">/</span>
        </template>
        <span class="ttl">{{ pageTitle }}</span>
      </div>
      <div class="spacer" />
      <PublicSiteStatusChip />
      <div class="wb-top-desktop">
        <StatusSwitch :value="status" @change="onStatusChange" />
      </div>
      <button class="wb-top-icon wb-logout-btn wb-top-desktop" type="button" title="退出登录" @click="logout">
        <span v-html="Icon.Logout" />
        <span class="lb">退出</span>
      </button>
    </header>

    <main class="wb-main">
      <router-view />
    </main>

    <nav class="wb-m-tabbar" aria-label="快捷导航">
      <button
        v-for="t in MOBILE_TABS"
        :key="t.path"
        type="button"
        :class="['wb-m-tab', activePath === t.path && !navOpen ? 'active' : '']"
        @click="go(t.path)"
      >
        <span v-html="t.icon" />
        <span class="lb">{{ t.label }}</span>
      </button>
      <button
        type="button"
        :class="['wb-m-tab', navOpen || (activePath && !MOBILE_TABS.some((t) => t.path === activePath)) ? 'active' : '']"
        @click="navOpen ? closeNav() : openNav()"
      >
        <span v-html="Icon.Menu" />
        <span class="lb">菜单</span>
      </button>
    </nav>
  </div>
</template>

<style scoped>
.logo-img {
  width: 28px; height: 28px;
  border-radius: var(--radius-m);
  object-fit: cover;
  flex-shrink: 0;
}
.av-img {
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: cover;
  display: block;
}
.nav-ic { display: inline-flex; }
.nav-ic :deep(svg) { width: 18px; height: 18px; flex-shrink: 0; }
.wb-m-tab :deep(svg) { width: 22px; height: 22px; }
</style>
